import { defineStore } from "pinia";
import { usePermissionStore } from "@/store/perms.js";
import { useRememberStore } from "@/store/remember.js";
import AxiosClass from "../classes/axios.js";
import RedisService from '@/classes/redisService.js';
import router from '@/router/index.js';
import { useEnumsStore } from "./enums.js";
import { i18n } from '@/i18n'

const axios = new AxiosClass();

export const useAuthUserStore = defineStore('authUser', {
    state: () => ({
        userInfo: null,
        maintenance: {},
        maintenanceToday: {},
        maintenanceInterval: null,
        isThrottled: false,
        currentRoute: null,
        redisService: null,
        remoteServiceFail: false,
    }),
    getters: {
        isLoggedIn: (state) => state.userInfo !== null,
        fullUserDetails: (state) => {
            return { ...state.userInfo?.user }
        },
        isUserDev: (state) => {
            return state.userInfo?.user.is_dev;
        },
        isMaintenance: (state) => {
            return Object.keys(state.maintenance !== undefined ? state.maintenance : {}).length > 0;
        },
        isMaintenanceToday: (state) => {
            return Object.keys(state.maintenanceToday !== undefined ? state.maintenanceToday : {}).length > 0;
        }
    },
    actions: {
        async login(credentials) {
            if (this.userInfo !== null) throw new Error('Vous êtes déjà connecté');
            try {
                await axios.post('/login', credentials).then(async (data) => {
                    if (data === undefined) throw new Error('Connexion impossible');
                    await this.setUserData(data);
                    
                    if (this.userInfo === null) { return false; }
                    this.initRedisService(data.user);
                    this.setUserLocale();
                });

                return true;
            } catch (err) {
                return err;
            }
        },
        initRedisService(user) {
            // Initialize RedisService
            this.redisService = new RedisService(user.id);

            this.redisService.socket.emit('userConnected', { user: user, route: window.location.pathname });
            this.redisService.listenForKeyUpdates("DisconnectUser", this.handleDisconnectUsers);

            // Setup beforeunload event
            window.addEventListener('beforeunload', this.handleBeforeUnload.bind(this));
        },

        handleBeforeUnload() {
            if (this.redisService) {
                this.redisService.disconnect();
                this.redisService = null; // Clean up RedisService instance
            }
        },

        handleDisconnectUsers(value) {
            if (value !== null && (this.userInfo?.user.is_dev ||
                (value !== "All" && parseInt(value) !== this.userInfo?.user.id))) { return; }

            this.logout().then(() => {
                router.push({name: 'Login'});
            });
        },

        async logout () {
            window.removeEventListener('beforeunload', this.handleBeforeUnload.bind(this));
            const permissionStore = usePermissionStore();
            await axios.get('/logout').then(() => {
                this.stopMaintenanceInterval();
                this.$reset();
                permissionStore.clear();
                this.getMaintenance(true);
            });
            return true;
        },

        async setUserData (userData) {
            if (this.isMaintenance && !userData.user.is_dev) { this.logout(); return; }
            const permissionStore = usePermissionStore();

            this.userInfo = userData;
            // this.setTokenInfo(userData.token);
            await permissionStore.getUserPermissions(userData?.user.role_id);
        },

        async setUserLocale() {
            const languagesEnum = useEnumsStore().Languages;
            const userLanguage =  await useEnumsStore().findInEnum(languagesEnum, 'id', this.userInfo?.user.language_id)?.locale;    
            const rememberedLanguage = useRememberStore().getFieldValue('Navbar', 'language');
            
            if (i18n.global.availableLocales.includes(rememberedLanguage)) {
                i18n.global.locale = rememberedLanguage;
            } else if (i18n.global.availableLocales.includes(userLanguage)) {
                i18n.global.locale = userLanguage;
            }
        },

        setTokenInfo (token) {
            axios.setTokenInfo(token);
        },

        async getMaintenance(isForce = false) {
            if (isForce || Object.keys(this.maintenance).length === 0) {
                await axios.get('/MaintenanceCurrent').then((response) => {
                    this.maintenance = response;
                });
            }

            if (isForce || Object.keys(this.maintenanceToday).length === 0) {
                await axios.get('/MaintenanceToday').then((response) => {
                    this.maintenanceToday = response;
                });
            }
        },

        stopMaintenanceInterval() {
            clearInterval(this.maintenanceInterval);
            this.maintenanceInterval = null;
        },

        updateCurrentRoute(route) {  // Add this action
            this.currentRoute = route; 
            this.setUserLocale(); 
            if (this.redisService) {
                this.redisService.handleRouteChange(this.userInfo, route);
            } else {
                this.initRedisService(this.userInfo);
            }
        },

        userHasSpecialization(specializationId) {
            return this.userInfo?.user?.specializations?.some(specialization => specialization.id === specializationId);
        }
    },
    persist: {
        storage: sessionStorage,
        paths: ['userInfo', 'maintenance', 'maintenanceToday', 'isThrottled', 'currentRoute', 'redisService', 'showWarningPopup', 'timeout', 'warningTimeout'],
    },
})